import {isFeatureEnabled} from '@github-ui/feature-flags'
import {searchUrl} from '@github-ui/issue-url-helper'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {noop} from '@github-ui/noop'
import {Link} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import type {Icon} from '@primer/octicons-react'
import {NavList, useConfirm} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import debounce from 'lodash-es/debounce'
import {useCallback, useMemo} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {useNavigationContext} from '../../contexts/NavigationContext'
import {
  useDirtySearchQuery,
  useQueryContext,
  useQueryEditState,
  useQueryMutations,
  useQueryViewEditFields,
  useSetCurrentPage,
} from '../../contexts/QueryContext'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import {commitRemoveUserViewMutation} from '../../mutations/remove-user-view-mutation'
import {iconToPrimerIcon} from './IconHelper'
import styles from './SavedViewItem.module.css'

// Track pending navigation for cancellation
let pendingNavigationId: number | null = null
const DEBOUNCE_MS = 1000

type Props = {
  id?: string
  icon?: string
  // progressively migrate to Icon
  Icon?: Icon
  color: string
  title: string
  description?: string
  query?: string
  position: number
}

export function SavedViewItem({id, icon, Icon, color, title, position, query}: Props) {
  const {setViewPosition, currentViewId} = useQueryContext()
  const {setCanEditView, isNewView, setIsNewView, setIsEditing, dirtyViewId} = useQueryEditState()
  const setCurrentPage = useSetCurrentPage()
  const {dirtyDescription, dirtyTitle} = useQueryViewEditFields()
  const dirtySearchQuery = useDirtySearchQuery()
  const {clearSavedViewEditState} = useQueryMutations()
  const {closeNavigation} = useNavigationContext()
  const {navigateToUrl} = useAppNavigate()
  const relayEnvironment = useRelayEnvironment()

  const navigate = useNavigate()
  const confirm = useConfirm()
  const isCurrent = id === currentViewId
  const url = searchUrl({viewId: id, query})

  const scopedRepoSubmitQuery = useCallback(async () => {
    navigateToUrl(url)
    setCurrentPage(1)
  }, [navigateToUrl, url, setCurrentPage])

  const deleteDirtyView = useCallback(() => {
    if (dirtyViewId === undefined) {
      return
    }

    const payload = {
      environment: relayEnvironment,
      input: {shortcutId: dirtyViewId},
      onError: noop,
      onCompleted: noop,
    }

    commitRemoveUserViewMutation(payload)
  }, [dirtyViewId, relayEnvironment])

  const submitQuery = useCallback(async () => {
    const isOptimizationEnabled = isFeatureEnabled('issues_dashboard_inp_optimization')

    // Skip if a navigation is already pending
    if (isOptimizationEnabled && pendingNavigationId !== null) {
      return
    }

    // In a repo's index, we don't want to navigate, just update the query
    // and update the UI
    if (id === VIEW_IDS.repository) {
      return scopedRepoSubmitQuery()
    }

    // When navigating to a different saved view while the user is editing a newly created view
    if (isNewView && (dirtyTitle !== LABELS.views.defaultName || dirtySearchQuery !== '' || dirtyDescription !== '')) {
      const discardChanges = await confirm({
        title: LABELS.views.unsavedChangesTitle,
        content: LABELS.views.unsavedChangesContent,
        confirmButtonType: 'danger',
      })

      if (!discardChanges) {
        return
      }

      deleteDirtyView()
    }

    setIsEditing(false)
    setIsNewView(false)

    clearSavedViewEditState()

    setViewPosition(position)
    setCanEditView(true)
    closeNavigation()

    // Defer navigation to idle time to improve INP (~160ms vs ~350-400ms before).
    //
    // Why requestIdleCallback? Profiling revealed that blocking processing time comes from
    // React Router's route reconciliation - navigating either tears down the page entirely
    // (for known views like /issues/mentioned) or re-renders the whole page from the root
    // (for custom views /issues/:viewId). This synchronous work blocks the main thread.
    //
    // Alternative approaches considered:
    // - Route consolidation: Keeping the component mounted and just updating props ran into
    //   stale data bugs. The routing and Relay store are tightly coupled - components rely
    //   on fresh store data with each route change. Fixing this would require deeper
    //   architectural changes (caching etc.), needs looking into.
    if (isOptimizationEnabled) {
      // Cancel any pending navigation before scheduling a new one
      if (pendingNavigationId !== null) {
        cancelIdleCallback(pendingNavigationId)
      }
      pendingNavigationId = requestIdleCallback(() => {
        pendingNavigationId = null
        navigate(url)
      })
    } else {
      navigate(url)
    }
  }, [
    id,
    isNewView,
    dirtyTitle,
    dirtySearchQuery,
    dirtyDescription,
    setIsEditing,
    setIsNewView,
    navigate,
    url,
    clearSavedViewEditState,
    setViewPosition,
    position,
    setCanEditView,
    closeNavigation,
    scopedRepoSubmitQuery,
    confirm,
    deleteDirtyView,
  ])

  const handleSelect = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      submitQuery()
    },
    [submitQuery],
  )

  // Debounce to prevent double-click INP spikes
  const onSelect = useMemo(() => debounce(handleSelect, DEBOUNCE_MS, {leading: true, trailing: false}), [handleSelect])

  return (
    <NavList.Item
      to={url}
      as={Link}
      aria-current={isCurrent ? 'page' : undefined}
      onClick={onSelect}
      className={styles.navItem}
    >
      <div className={styles.itemText}>
        <div className={styles.icon} data-color={color.toLowerCase()}>
          {Icon ? <Icon /> : icon ? <Octicon icon={iconToPrimerIcon(icon)!} /> : null}
        </div>
        <span className={styles.truncatedItemText}>{title}</span>
      </div>
    </NavList.Item>
  )
}
